import React, { FC, Fragment, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import PageTitle from 'components/shared/page-title/page-title.component';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import CreateReleaseNoteDialog from 'components/release-notes/create-release-note-dialog/create-release-note-dialog.component';
import EditReleaseNoteDialog from 'components/release-notes/edit-release-note-dialog/edit-release-note-dialog.component';
import { getReleaseNotesTemplate, updateReleaseNoteTemplate } from 'apollo/templates/release-notes';
import { ReleaseNote, ReleaseNoteOrganizationType } from 'models/release-note';
import { formatDate } from 'utils/format';

const ORG_TYPE_LABEL: Record<ReleaseNoteOrganizationType, string> = {
    [ReleaseNoteOrganizationType.all]: 'ALL',
    [ReleaseNoteOrganizationType.clinic]: 'CLINIC',
    [ReleaseNoteOrganizationType.lab]: 'LAB',
};

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 60 },
    { type: 'text', width: 100 },
    { type: 'rectangular', width: 36, height: 20 },
    { type: 'text', width: 90 },
    { type: 'button', width: 32, height: 32 },
];
const ReleaseNotes: FC = () => {
    const { t: translate } = useTranslation(['release-notes', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingReleaseNote, setEditingReleaseNote] = useState<ReleaseNote | null>(null);
    const [publishingIds, setPublishingIds] = useState<Set<string>>(new Set());

    const { data, loading } = useQuery<{ releaseNotes: ReleaseNote[] }>(getReleaseNotesTemplate, {
        onError: (error) => showBoundary(error),
        fetchPolicy: 'cache-and-network',
    });

    const [updateReleaseNote] = useMutation(updateReleaseNoteTemplate, {
        onError: (error) => showBoundary(error),
        refetchQueries: [{ query: getReleaseNotesTemplate }],
    });

    const handleTogglePublish = async (clickEvent: React.MouseEvent, releaseNote: ReleaseNote) => {
        clickEvent.stopPropagation();
        setPublishingIds((previous) => new Set(previous).add(releaseNote._id));
        try {
            await updateReleaseNote({
                variables: {
                    id: releaseNote._id,
                    releaseNote: {
                        organizationType: releaseNote.organizationType,
                        translations: releaseNote.translations,
                        isPublished: !releaseNote.isPublished,
                    },
                },
            });
        } finally {
            setPublishingIds((previous) => {
                const nextIds = new Set(previous);
                nextIds.delete(releaseNote._id);
                return nextIds;
            });
        }
    };

    const releaseNotes = data?.releaseNotes ?? [];

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('release-notes:RELEASE_NOTES')} />

            <Box mb={1} display="flex" justifyContent="flex-end" alignItems="center">
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateDialogOpen(true)}>
                    {translate('release-notes:CREATE_RELEASE_NOTE')}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('release-notes:VERSION')}</TableCell>
                            <TableCell>{translate('release-notes:ORGANIZATION_TYPE')}</TableCell>
                            <TableCell>{translate('release-notes:PUBLISHED')}</TableCell>
                            <TableCell>{translate('release-notes:CREATED_AT')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && <SkeletonTable columns={skeletonColumns} rows={5} renderRowsOnly={true} />}
                        {!loading && releaseNotes.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                        {translate('release-notes:NO_RELEASE_NOTES')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {releaseNotes.map((releaseNote) => (
                            <TableRow
                                key={releaseNote._id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => setEditingReleaseNote(releaseNote)}
                            >
                                <TableCell>{releaseNote.version}</TableCell>
                                <TableCell>
                                    {translate(`common:${ORG_TYPE_LABEL[releaseNote.organizationType]}`)}
                                </TableCell>
                                <TableCell>
                                    <Tooltip
                                        title={
                                            releaseNote.isPublished
                                                ? translate('release-notes:STATUS_PUBLISHED')
                                                : translate('release-notes:STATUS_UNPUBLISHED')
                                        }
                                        placement="bottom"
                                    >
                                        <Switch
                                            checked={releaseNote.isPublished}
                                            disabled={publishingIds.has(releaseNote._id)}
                                            onClick={(clickEvent) => handleTogglePublish(clickEvent, releaseNote)}
                                            size="small"
                                            inputProps={{
                                                'aria-label': releaseNote.isPublished
                                                    ? translate('release-notes:STATUS_PUBLISHED')
                                                    : translate('release-notes:STATUS_UNPUBLISHED'),
                                            }}
                                        />
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{formatDate(releaseNote.createdAt, 'date')}</TableCell>
                                <TableCell>
                                    <Box display="flex" justifyContent="flex-end">
                                        <Tooltip title={translate('common:EDIT')} placement="bottom">
                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setEditingReleaseNote(releaseNote);
                                                }}
                                                aria-label="edit"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <CreateReleaseNoteDialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} />

            {editingReleaseNote && (
                <EditReleaseNoteDialog
                    open={Boolean(editingReleaseNote)}
                    onClose={() => setEditingReleaseNote(null)}
                    releaseNoteData={editingReleaseNote}
                />
            )}
        </Fragment>
    );
};

export default ReleaseNotes;
